import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  FormGroup,
  FormGroupProps,
  FormHelperText,
  FormHelperTextProps,
  FormLabel,
  FormLabelProps,
  styled,
} from '@mui/material';
import { Field, useField } from 'react-final-form';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import { hasError } from './utils';

import { Switch, SwitchProps } from '../components';

export interface SwitchItem {
  value?: unknown;
  label?: React.ReactNode;
  disabled?: boolean;
}

export interface SwitchesProps {
  name: string;
  data: SwitchItem | SwitchItem[];
  label?: React.ReactNode;
  helperText?: string;
  required?: boolean;
  // it's a way to pass props to internal components
  fieldProps?: Partial<Parameters<typeof Field>[0]>;
  formControlLabelProps?: Partial<FormControlLabelProps>;
  formControlProps?: Partial<FormControlProps>;
  formGroupProps?: Partial<FormGroupProps>;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  formLabelProps?: Partial<FormLabelProps>;
  switchProps?: Partial<SwitchProps>;
}

interface SwitchWrapperProps {
  name: string;
  data: SwitchItem;
  isSingle: boolean;
  fieldProps: SwitchesProps['fieldProps'];
  formControlLabelProps: SwitchesProps['formControlLabelProps'];
  switchProps: SwitchesProps['switchProps'];
}

const SwitchWrapper: React.FC<SwitchWrapperProps> = (props) => {
  const { name, data, isSingle, formControlLabelProps, switchProps } = props;
  const { isEditable } = useCanEditFormFieldsContext();

  function renderField() {
    const { name, data: fieldData, fieldProps } = props;
    const isDisabled = fieldData.disabled || !isEditable;

    return (
      <Field
        type='checkbox'
        name={name}
        render={(fieldProps) => {
          const {
            input: { name, value, onChange, checked },
          } = fieldProps;
          return (
            <Switch
              name={name}
              value={value}
              onChange={onChange}
              checked={checked}
              disabled={isDisabled}
              {...switchProps}
            />
          );
        }}
        {...fieldProps}
      />
    );
  }

  if (!data.label) {
    return renderField();
  }

  return (
    <FormControlLabel
      label={data.label}
      name={name}
      disabled={data.disabled}
      value={isSingle ? undefined : data.value}
      control={renderField()}
      {...formControlLabelProps}
    />
  );
};

const FormControlStyled = styled(FormControl)({ alignItems: 'flex-end' });

export const Switches: React.FC<SwitchesProps> = (props) => {
  const {
    label,
    data,
    name,
    required,
    fieldProps,
    formControlLabelProps,
    formControlProps,
    formGroupProps,
    formHelperTextProps,
    formLabelProps,
    switchProps,
  } = props;
  const { meta } = useField(name);
  const isError = hasError(meta);

  const items = Array.isArray(data) ? data : [data];
  const isSingle = items.length === 1;
  const { error, submitError } = meta;
  const errorText = error || submitError;
  const helperText = isError ? errorText : props.helperText;

  return (
    <FormControlStyled error={isError} required={required} {...formControlProps}>
      {label && <FormLabel {...formLabelProps}>{label}</FormLabel>}
      <FormGroup {...formGroupProps}>
        {items.map((item, index) => {
          return (
            <SwitchWrapper
              key={index}
              name={name}
              data={item}
              isSingle={isSingle}
              fieldProps={fieldProps}
              formControlLabelProps={formControlLabelProps}
              switchProps={switchProps}
            />
          );
        })}
      </FormGroup>
      <FormHelperText {...formHelperTextProps}>{helperText}</FormHelperText>
    </FormControlStyled>
  );
};
